/**
 * src/api/consentApi.ts — customer agreement gate (web).
 *
 * Mirrors packages/core/src/consent.ts in attic-mobile against the same two
 * endpoints, so the two clients can never disagree about what's required. The
 * client never decides what's outstanding and never supplies the assent
 * string — both come from the server, so UI copy can't drift away from the
 * evidence we keep.
 */
import { getOne, HarperError, post } from './harperClient';

export type ConsentState = 'current' | 'pending' | 'declined';
export type ConsentDocType = 'tos' | 'privacy' | 'workforce_privacy';
export type AttestationType = 'age_attestation';

export interface LegalDocument {
  uuid: string;
  doc_type: ConsentDocType;
  version: string;
  title: string;
  body_markdown: string;
  content_hash: string;
  effective_at: string;
  change_summary: string | null;
  /** Verbatim label for the accept control. Server-owned. */
  assent_text: string;
  /** Terms: true. Privacy: false. */
  requires_scroll: boolean;
  /**
   * The version of THIS document the customer last accepted, or null if they
   * never have. Server-computed — the client can't infer it from
   * `change_summary`, since a first release can carry one too. Drives
   * "we've updated our Terms" vs "before you start".
   */
  previously_accepted_version: string | null;
}

export interface ConsentStatus {
  role: string;
  consent_state: ConsentState | null;
  outstanding: ConsentDocType[];
  attestations: AttestationType[];
  documents: LegalDocument[];
  attestation_copy: Record<string, string>;
}

export interface RecordConsentResult {
  recorded: number;
  consent_state: ConsentState;
  outstanding: ConsentDocType[];
  attestations: AttestationType[];
}

/**
 * Backoff between attempts. Three attempts total, ~1.6s worst case before the
 * gate gives up and blocks. Mirrors packages/core/src/consent.ts in
 * attic-mobile — keep the two in step.
 */
const RETRY_DELAYS_MS = [400, 1200];

/**
 * Is this failure worth another attempt?
 *
 * A thrown value that isn't a HarperError never reached the server (DNS,
 * dropped socket, timeout) — exactly the case worth retrying. A HarperError
 * carries the server's answer: 5xx may be transient, but 4xx is definitive and
 * retrying only delays the error screen.
 */
function isRetryable(e: unknown): boolean {
  if (!(e instanceof HarperError)) return true;
  return e.status >= 500;
}

/**
 * What the signed-in caller still has to agree to.
 *
 * Throws on failure rather than returning an empty status — the gate must be
 * able to tell "nothing outstanding" apart from "couldn't ask", and treat the
 * second as blocking.
 *
 * Retries transient failures before throwing. The gate fails CLOSED, so one
 * dropped request used to put a hard error screen in front of a customer who
 * had already consented — a total block on the app, harsher than an explicit
 * decline (which stays read-only). Retrying does not resolve that asymmetry;
 * it stops the cheapest failure from triggering it.
 */
export async function getConsentStatus(): Promise<ConsentStatus> {
  let lastError: unknown;
  for (let attempt = 0; ; attempt++) {
    try {
      const status = await getOne<ConsentStatus>('/LegalDocuments');
      // getOne turns a 404 into null. That must not become EMPTY: EMPTY has
      // documents: [], which both gates read as "nothing published yet" and
      // wave the customer through with no record. /LegalDocuments 404s for a
      // real reason — "Customer record not found for caller" — and a missing
      // Customer row is the last state that should bypass the gate. Throw, so
      // it takes the same blocked-or-cached path as any other failure.
      if (!status) throw new HarperError(404, 'Consent status unavailable');
      return status;
    } catch (e) {
      lastError = e;
      if (attempt >= RETRY_DELAYS_MS.length || !isRetryable(e)) break;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt] ?? 0));
    }
  }
  throw lastError;
}

/** Record an affirmative act. 409 means the documents moved — refetch and re-present. */
export async function recordConsent(input: {
  action: 'accepted' | 'declined';
  documents: Pick<LegalDocument, 'doc_type' | 'uuid' | 'content_hash' | 'version'>[];
  attestations?: AttestationType[];
}): Promise<RecordConsentResult> {
  return post<RecordConsentResult>('/RecordConsent', {
    action: input.action,
    documents: input.documents.map((d) => ({
      doc_type: d.doc_type,
      document_id: d.uuid,
      content_hash: d.content_hash,
      version: d.version,
    })),
    attestations: input.attestations ?? [],
    method: 'clickwrap_button',
    device: {
      platform: 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    },
  });
}

export function isConsentSatisfied(status: ConsentStatus): boolean {
  return (
    status.consent_state === 'current' &&
    status.outstanding.length === 0 &&
    status.attestations.length === 0
  );
}
